<template>
  <div class="chart-container">
    <div class="chart-header">
      <Dropdown
        v-if="availableSorts.length > 1"
        :titleDropdown="selectedSort.name"
        position="right"
        colorArrow="black"
      >
        <ul>
          <li
            v-for="sort in availableSorts"
            :key="sort.type"
            @click="changeSortType(sort.type)"
            class="bkps-dropdown-item"
            :title="sort.description"
          >
            <i :class="sort.icon"></i>
            {{ sort.name }}
          </li>
        </ul>
      </Dropdown>
    </div>
    <div ref="targetChart" style="height: 450px; width: 100%; margin-top: 10px;"></div>
  </div>
</template>

<script>
import Highcharts from 'highcharts/es5/highcharts'
import mapInit from 'highcharts/es5/modules/map'
import brMap from '@/config/br-states.topo.json'
import ChartMix from '@/components/charts/ChartMix.vue'
import ChartThemeMixin from '@/components/charts/ChartThemeMixin.vue'
import Dropdown from '@/components/shares/specialTag/Dropdown.vue'
import { stateNamesByCode } from '@/constants/states'

mapInit(Highcharts)
Highcharts.maps['brStates'] = brMap

export default {
  components: { Dropdown },
  props: {
    graphData: { required: true, type: Array },
    graphTitle: { required: true, type: String },
    loading: { required: true, type: Boolean },
  },
  data() {
    return {
      sortBy: 'count',
      availableSorts: [
        {
          type: 'count',
          name: 'Quantidade',
          icon: 'fas fa-hashtag',
          description: 'Visualizar por quantidade de multas'
        }
      ]
    }
  },
  computed: {
    selectedSort() {
      return this.availableSorts.find(sort => sort.type === this.sortBy)
    }
  },
  methods: {
    changeSortType(type) {
      this.sortBy = type
      this.createChart()
    },
    buildSeries() {
      return (this.graphData || []).map(item => {
        const count = item.count || 0
        const ufName = (item.name || stateNamesByCode[item.uf] || '').toUpperCase()

        return {
          'hc-a2': item.uf,
          ufName,
          count,
          value: count > 0 ? count : null
        }
      })
    },
    createChart() {
      if (!this.$refs.targetChart) return

      const existingChart = Highcharts.charts.find(chart =>
        chart && chart.renderTo === this.$refs.targetChart
      )
      if (existingChart) {
        existingChart.destroy()
      }
      this.$refs.targetChart.innerHTML = ''

      const theme = this.chartTheme
      const data = this.buildSeries()

      const chartConfig = {
        chart: {
          map: 'brStates',
          backgroundColor: theme.backgroundColor,
          style: { fontFamily: '"Montserrat", sans-serif' }
        },
        credits: { enabled: false },
        title: {
          text: this.currentTitle,
          align: 'center',
          style: {
            color: theme.textColor,
            fontSize: '18px',
            fontWeight: '600'
          }
        },
        mapNavigation: {
          enabled: true,
          buttonOptions: { verticalAlign: 'bottom' }
        },
        colorAxis: {
          min: 0,
          stops: [
            [0, '#2ecc71'],
            [0.25, '#a3d94f'],
            [0.5, '#f1c40f'],
            [0.75, '#e67e22'],
            [1, '#e74c3c']
          ],
          labels: { style: { color: theme.textColor } }
        },
        legend: {
          title: {
            text: 'Quantidade de multas',
            style: { color: theme.textColor }
          },
          itemStyle: { color: theme.textColor }
        },
        tooltip: {
          backgroundColor: theme.backgroundColor,
          borderColor: theme.gridLineColor,
          style: { color: theme.textColor },
          useHTML: true,
          formatter: function () {
            const p = this.point
            return `<b>${p['hc-a2']} - ${p.ufName}</b><br/>` +
              `Quantidade: ${Highcharts.numberFormat(p.count || 0, 0, ',', '.')}`
          }
        },
        series: [{
          type: 'map',
          name: this.graphTitle,
          data: data,
          joinBy: ['hc-a2', 'hc-a2'],
          nullColor: theme.gridLineColor,
          borderColor: theme.gridLineColor,
          dataLabels: {
            enabled: true,
            format: '{point.hc-a2}',
            style: {
              color: theme.textColor,
              fontSize: '10px',
              fontWeight: '600',
              textOutline: 'none'
            }
          },
          states: {
            hover: { borderColor: theme.textColor }
          }
        }]
      }

      Highcharts.mapChart(this.$refs.targetChart, chartConfig)
    }
  },
  mixins: [ChartMix, ChartThemeMixin],
  mounted() {
    this.createChart()
  },
  watch: {
    isDarkTheme: {
      handler() {
        this.$nextTick(() => {
          this.createChart()
        })
      },
      immediate: false
    },
    graphData: {
      handler() {
        this.$nextTick(() => {
          this.createChart()
        })
      },
      deep: true
    }
  },
  name: 'CmEmbarcadorEstadoChart'
}
</script>

<style scoped>
.chart-container {
  position: relative;
  width: 100%;
  border-radius: 12px;
  overflow: visible;
  background-color: var(--white-bg);
  padding: 15px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24);
}

.chart-header {
  position: absolute;
  top: 15px;
  right: 20px;
  z-index: 10;
  display: flex;
  gap: 8px;
}

/* CSS específico para o dropdown dos gráficos */
.chart-header .bkps-dropdown {
  background: var(--white-bg) !important;
  border: 1px solid var(--border-color) !important;
  color: var(--primary-text-color) !important;
}

.chart-header .bkps-dropdown-content {
  background-color: var(--white-bg) !important;
  color: var(--primary-text-color) !important;
  border: 1px solid var(--border-color) !important;
}

.chart-header .bkps-dropdown-item {
  color: var(--primary-text-color) !important;
}

.chart-header .bkps-dropdown-item:hover {
  background-color: var(--btn-hover-bg) !important;
}

.chart-header .hide {
  max-height: 0 !important;
  padding: 0 !important;
  border: none !important;
  box-shadow: none !important;
  margin: 0 !important;
}
</style>
